import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SafeImg } from "@/components/SafeImg";
import {
  Home,
  ShieldCheck,
  Clock,
  Check,
  Star,
  ArrowRight,
  MapPin,
  Phone,
  CalendarDays,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { PlaceholderImage } from "@/components/layout/PlaceholderImage";
import { SlideshowSpot } from "@/components/SlideshowSpot";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import heroFallbackAsset from "@/assets/hero-fallback.jpg.asset.json";
import { sb } from "@/lib/sb";
import { getGoogleReviews, type GoogleReview } from "@/lib/google-reviews.functions";
import { buildSrcSet, pickSrc, type VariantMap } from "@/lib/image-srcset";
import Autoplay from "embla-carousel-autoplay";


const heroFallback = heroFallbackAsset.url;


const HOME_TITLE = "Homestead Construction | Searcy AR Remodeling & Builds";
const HOME_DESC =
  "Home remodeling, additions, basement finishing, and custom builds in Searcy and Northeast Arkansas. Quality craftsmanship. Free consultations.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: HOME_TITLE },
      { name: "description", content: HOME_DESC },
      { property: "og:title", content: HOME_TITLE },
      { property: "og:description", content: HOME_DESC },
      { property: "og:url", content: "https://homesteadconstruction.us/" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: HOME_TITLE },
      { name: "twitter:description", content: HOME_DESC },
    ],
    links: [
      { rel: "canonical", href: "https://homesteadconstruction.us/" },
      { rel: "preload", as: "image", href: heroFallback, fetchPriority: "high" },
    ],
  }),
  component: HomePage,
});

const services = [
  {
    title: "Home Upgrades",
    body: "Enhance your property's value and functionality with expertly designed and constructed home upgrades, including new living spaces and seamless property extensions.",
    cta: "Explore Home Upgrades",
    image: "Home Upgrades",
    slotKey: "home.service.upgrades",
  },
  {
    title: "Remodels",
    body: "Transform your current spaces into modern, and functional, environments. Our comprehensive remodeling services includes interior remodeling, bathroom and kitchen.",
    cta: "See Remodeling Services",
    image: "Remodels",
    slotKey: "home.service.remodels",
  },
  {
    title: "Finishing & Exterior",
    body: "Enhance the aesthetics and durability of your home with our expert finishing and exterior services. Our finishing and exterior services includes flooring, painting, and decks.",
    cta: "Explore Exterior Services",
    image: "Finishing & Exterior",
    slotKey: "home.service.exterior",
  },
  {
    title: "Custom Projects",
    body: "We handle a wide range of custom construction needs for homeowners, always delivering exceptional results tailored to your unique specifications. Don't hesitate to reach out!",
    cta: "Discover Custom Projects",
    image: "Custom Projects",
    slotKey: "home.service.custom",
  },
];

const valueProps = [
  {
    icon: Home,
    title: "Building Trust Through Quality Work and Customer Focus",
    body: "We prioritize your vision, delivering exceptional results tailored to your needs.",
  },
  {
    icon: ShieldCheck,
    title: "Integrity and Transparency: Our Commitment to You",
    body: "We communicate openly, ensuring you are informed every step of the way.",
  },
  {
    icon: Clock,
    title: "Reliability You Can Count On: Efficient, and Timely Service",
    body: "Our team is dedicated to maintaining a clean job site and punctuality.",
  },
];

const setsApart = [
  "Integrity & Transparency",
  "Predictable & Positive Outcomes",
  "Efficient & Respectful Work",
  "Reliability & Communication",
];

const reviews = [
  {
    name: "Jason Gingerich",
    when: "8 months ago",
    text: "Look at what homestead construction built me - top quality job at a fast reasonable price! Honest and I highly recommend.",
  },
  {
    name: "Lee & Susan Card",
    when: "10 months ago",
    text: "Had the best experience working with Wes and his team. Pricing was fair and work was exceptional. What started as just an emergency shower stall replacement, developed into a...",
  },
];

type PublicProject = {
  id: string;
  name: string;
  category: string;
  photos: string[];
  primary_photo_index: number;
  before_photo: string | null;
  after_photo: string | null;
  before_label: string | null;
  after_label: string | null;
  before_after_enabled: boolean;

  photo_variants: Record<string, VariantMap> | null;
};



const heroSlides = [
  { slotKey: "home.hero.1", label: "Hero Slide 1" },
  { slotKey: "home.hero.2", label: "Hero Slide 2" },
  { slotKey: "home.hero.3", label: "Hero Slide 3" },
  { slotKey: "home.hero.4", label: "Hero Slide 4" },
  { slotKey: "home.hero.5", label: "Hero Slide 5" },
];

function HomePage() {
  return (
    <>
      <Hero />
      <ValueTrio />
      <ServicesOverview />
      <AboutPreview />
      <Reviews />
      <Projects />
      <CtaBand />
      <ContactSection />
    </>
  );
}

function Hero() {
  return (
    <section className="relative bg-ink text-white overflow-hidden">
      <div className="absolute inset-0">
        <SlideshowSpot
          slideshowKey="home.hero"
          fallbackSlotKeys={heroSlides.map((s) => s.slotKey)}
          label="Home hero"
          kenBurns
          priority
          fallbackSrc={heroFallback}
          sizes="100vw"
        />

      </div>

      <div className="absolute inset-0 bg-ink/70 pointer-events-none" />
      <div className="container mx-auto px-4 py-24 md:py-36 relative">
        <div className="max-w-2xl">
          <p className="eyebrow text-brand">Your Vision. Our Craft.</p>
          <h1 className="mt-4 font-display font-bold uppercase text-white text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Transforming your home with precision and trust.
          </h1>
          <p className="mt-6 text-white/85 text-base md:text-lg max-w-xl leading-relaxed">
            At Homestead Construction of NEA, we turn your home improvement dreams into reality.
            From stunning remodels to seamless additions, we provide complete turn-key construction
            services, ensuring every detail reflects your vision with precision and excellence.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button asChild variant="brand" size="lg">
              <Link to="/services">Services</Link>
            </Button>
            <Button asChild variant="ghostOnDark" size="lg">
              <a href="tel:5015936361"><Phone className="h-4 w-4" /> Call Us</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValueTrio() {
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        {valueProps.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="bg-surface-alt p-10 md:p-12 text-center flex flex-col items-center"
          >
            <Icon className="h-16 w-16 text-brand mb-6" strokeWidth={2.25} />
            <h2 className="font-display font-bold uppercase text-xl md:text-2xl leading-tight text-ink mb-4">
              {title}
            </h2>
            <p className="text-base text-ink/75 leading-relaxed max-w-xs">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}


function ServicesOverview() {
  return (
    <section className="bg-surface-alt py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto">
          <p className="eyebrow">Our Services</p>
          <h2 className="mt-3 font-display font-bold uppercase text-3xl md:text-4xl leading-tight text-ink">
            Comprehensive Home Improvement Services Tailored to Your Lifestyle
          </h2>
          <p className="mt-4 text-sm text-ink/60 font-semibold tracking-wide">
            Business License Number 0450850225
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <article key={s.title} className="flex flex-col">
              <div className="aspect-[3/4] overflow-hidden">
                <PlaceholderImage
                  slotKey={s.slotKey}
                  label={s.image}
                  sizes="(min-width:1024px) 25vw, (min-width:640px) 50vw, 100vw"
                />
              </div>

              <div className="pt-6 flex flex-col flex-1">
                <h3 className="font-display font-bold uppercase text-xl text-ink">{s.title}</h3>
                <p className="mt-4 text-sm text-ink/75 leading-relaxed">{s.body}</p>
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="eyebrow">About Us</p>
          <h2 className="mt-3 font-display font-bold uppercase text-3xl md:text-4xl leading-tight text-ink">
            Our Commitment to Quality, Integrity, and Your Vision
          </h2>
          <p className="mt-6 text-ink/75 leading-relaxed">
            Wesley Rhodes has been immersed in the construction industry since 2009, beginning his
            journey at the age of 16. He honed his skills working alongside an Amish crew for
            several years, where he was instilled with core values of hard work, honesty, and
            quality craftsmanship. In 2020, Wesley brought this foundation to Arkansas,
            establishing his own business. We genuinely enjoy our work and are passionately
            committed to making your home improvement dreams a tangible reality.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display font-bold uppercase text-sm text-ink">
                Customer-Centric Focus
              </h3>
              <p className="mt-2 text-sm text-ink/70">
                We are dedicated to bringing your visions and dreams to life.
              </p>
            </div>
            <div>
              <h3 className="font-display font-bold uppercase text-sm text-ink">
                Uncompromising Quality
              </h3>
              <p className="mt-2 text-sm text-ink/70">
                We strive for precision and excellence in every project.
              </p>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-display font-bold uppercase text-sm text-ink mb-4">
              What Sets Us Apart
            </h3>
            <ul className="space-y-2">
              {setsApart.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-ink/80">
                  <span className="h-5 w-5 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="relative">
          <div className="aspect-[4/5] w-full">
            <SlideshowSpot
              slideshowKey="home.about"
              fallbackSlotKeys={["home.about"]}
              label="IMG_1647 — Owner / interior portrait"
              sizes="(min-width:1024px) 50vw, 100vw"
            />
          </div>

          <div className="absolute -bottom-6 -left-6 sm:bottom-6 sm:left-6 bg-brand text-white px-6 py-5 text-center shadow-xl">
            <div className="font-display font-bold text-3xl leading-none">16</div>
            <div className="font-display font-semibold uppercase text-[10px] tracking-widest mt-1">
              Years of Experience
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const [liveReviews, setLiveReviews] = useState<GoogleReview[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      getGoogleReviews()
        .then((p) => {
          if (!cancelled && Array.isArray(p?.reviews) && p.reviews.length > 0) {
            setLiveReviews(p.reviews);
          }
        })
        .catch(() => {
          /* keep fallback */
        });
    };
    const w = window as any;
    const handle = w.requestIdleCallback
      ? w.requestIdleCallback(run, { timeout: 3000 })
      : window.setTimeout(run, 1200);
    return () => {
      cancelled = true;
      if (w.cancelIdleCallback && w.requestIdleCallback) w.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  const items = liveReviews
    ? liveReviews.map((r) => ({
        name: r.author_name,
        when: r.relative_time_description,
        text: r.text,
        rating: Math.max(1, Math.min(5, Math.round(r.rating || 5))),
      }))
    : reviews.map((r) => ({ ...r, rating: 5 }));

  return (
    <section className="bg-surface-alt py-16 md:py-24">
      <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-start">
        <div>
          <p className="eyebrow">Our Reviews</p>
          <h2 className="mt-3 font-display font-bold uppercase text-3xl md:text-4xl leading-tight text-ink">
            What our clients say
          </h2>
        </div>

        <Carousel
          opts={{ align: "start", loop: true, duration: 60, watchDrag: true, dragFree: false }}
          plugins={[Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })]}
          className="w-full touch-pan-y select-none"
        >
          <CarouselContent>
            {items.map((r, idx) => (
              <CarouselItem key={`${r.name}-${idx}`} className="md:basis-1/2">
                <div className="bg-white p-6 h-full">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold">
                        {r.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-ink">{r.name}</div>
                        <div className="flex gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3.5 w-3.5 ${i < r.rating ? "fill-yellow-400 text-yellow-400" : "text-yellow-400/30"}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <GoogleG />
                  </div>
                  <p className="mt-4 text-sm text-ink/75 leading-relaxed">{r.text}</p>
                  <div className="mt-4 text-xs text-ink/70">{r.when}</div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-end gap-2 mt-6">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}


function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.6 39.6 16.3 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.2 5.2c-.4.4 6.5-4.8 6.5-14.8 0-1.3-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

function ProjectPhoto({ project }: { project: PublicProject }) {
  const photos = project.photos ?? [];
  const startIdx = Math.min(
    Math.max(project.primary_photo_index ?? 0, 0),
    Math.max(photos.length - 1, 0),
  );
  // Build a rotation starting from the primary photo, then remaining in order.
  const order = photos.length
    ? [startIdx, ...photos.map((_, i) => i).filter((i) => i !== startIdx)]
    : [];
  const [attempt, setAttempt] = useState(0);

  if (photos.length === 0 || attempt >= order.length) {
    return <PlaceholderImage label={project.name} />;
  }

  const photo = photos[order[attempt]];
  const variants = project.photo_variants?.[photo] ?? null;
  const srcSet = buildSrcSet(variants);
  const src = pickSrc(variants, photo);

  return (
    <SafeImg
      key={photo}
      src={src}
      {...(srcSet ? { srcSet, sizes: "(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw" } : {})}
      alt={project.name}
      loading="lazy"
      decoding="async"
      width={800}
      height={1000}
      className="w-full h-full object-cover"
      onFail={() => setAttempt((a) => a + 1)}
    />
  );
}

function Projects() {

  const [projects, setProjects] = useState<PublicProject[]>([]);

  useEffect(() => {
    let cancelled = false;
    let retryId: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      if (cancelled) return;
      const { data, error } = await sb
        .from("projects")
        .select("id, name, category, photos, primary_photo_index, is_featured, date_completed, sort_order, before_photo, after_photo, before_label, after_label, before_after_enabled, photo_variants")
        .eq("is_published", true)
        .eq("is_featured", true)
        .order("sort_order", { ascending: true })
        .order("date_completed", { ascending: false, nullsFirst: false })
        .limit(12);

      if (cancelled) return;
      if (error) {
        retryId = setTimeout(() => void load(), 3000);
        return;
      }
      setProjects(((data as PublicProject[]) ?? []));
    };
    const w = window as any;
    const handle = w.requestIdleCallback
      ? w.requestIdleCallback(() => void load(), { timeout: 3000 })
      : window.setTimeout(() => void load(), 1500);
    return () => {
      cancelled = true;
      if (retryId) clearTimeout(retryId);
      if (w.cancelIdleCallback && w.requestIdleCallback) w.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  if (projects.length === 0) return null;

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <p className="eyebrow">Our Projects</p>
          <h2 className="mt-3 font-display font-bold uppercase text-3xl md:text-4xl leading-tight text-ink">
            Projects Completed
          </h2>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
            watchDrag: (_api, evt) =>
              !(evt.target as Element | null)?.closest("[data-noswipe]"),
          }}
          className="w-full"
        >
          <CarouselContent>
            {projects.map((p) => {
              const showBA = p.before_after_enabled && p.before_photo && p.after_photo;
              return (
                <CarouselItem key={p.id} className="md:basis-1/2 lg:basis-1/3">
                  <article className="relative">
                    <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                      {showBA ? (
                        <BeforeAfterSlider
                          before={p.before_photo!}
                          after={p.after_photo!}
                          alt={p.name}
                          beforeLabel={p.before_label || undefined}
                          afterLabel={p.after_label || undefined}
                        />

                      ) : (
                        <ProjectPhoto project={p} />
                      )}
                    </div>

                    <div className="absolute left-4 right-12 bottom-6 md:left-6 md:right-16 pointer-events-none">
                      {p.category && (
                        <span className="inline-block bg-brand text-white px-4 py-1.5 font-display font-semibold uppercase text-[11px] tracking-wider">
                          {p.category}
                        </span>
                      )}
                      <h3 className="bg-white text-ink font-display font-bold uppercase text-lg md:text-xl px-5 py-4 shadow-sm">
                        {p.name}
                      </h3>
                    </div>
                  </article>
                </CarouselItem>
              );
            })}
          </CarouselContent>


          <div className="flex justify-center gap-2 mt-8">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative bg-ink text-white overflow-hidden">
      <div className="absolute inset-0">
        <SlideshowSpot
          slideshowKey="home.cta"
          fallbackSlotKeys={["home.cta"]}
          label="3G5A6786 — CTA band exterior photo"
          sizes="100vw"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/70 to-ink/30" />
      </div>
      <div className="container mx-auto px-4 py-16 md:py-20 relative">
        <div className="max-w-xl">
          <p className="eyebrow text-brand">Your Vision. Our Craft.</p>
          <p className="mt-4 text-white text-xl md:text-2xl font-display font-semibold leading-snug">
            Transforming houses into dream homes with unparalleled quality and transparent service.
            Contact us to start your project today!
          </p>
          <Button asChild variant="brand" size="xl" className="mt-8">
            <a href="tel:5015936361">
              <Phone className="h-4 w-4" /> Call Us
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section id="contact-us" className="bg-surface-alt py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="eyebrow">Contact us</p>
          <h2 className="mt-3 font-display font-bold uppercase text-3xl md:text-4xl leading-tight text-ink">
            Let's Build Your Dream Home Together.
          </h2>
          <p className="mt-5 text-ink/75 leading-relaxed font-body text-lg">
            Ready to transform your home? The best way to get started is simple — give us a call.
            We enjoy talking through your ideas, answering questions, and figuring out what's
            possible for your home.
          </p>

          <div className="mt-8 flex justify-center">
            <Button asChild variant="brand" size="xl">
              <a href="tel:5015936361">
                <Phone className="h-4 w-4" /> Call Us
              </a>
            </Button>
          </div>

          <ul className="mt-12 space-y-6 text-left inline-block mx-auto">
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <MapPin className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Location</p>
                <p className="text-ink/70">Searcy, Arkansas USA</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Phone</p>
                <a href="tel:5015936361" className="text-ink/70 hover:text-brand">
                  501-593-6361
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="grid place-content-center h-12 w-12 rounded-full bg-brand text-white shrink-0">
                <CalendarDays className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display font-semibold uppercase text-ink">Hours</p>
                <p className="text-ink/70">Monday – Friday 7am to 5pm</p>
                <p className="text-ink/70">Closed Sunday</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
